import {Article, ArticleQuestion} from "@/model/Article";
import {stripTagsAndEntities} from "@/utils/string";

export function generateArticleSchemaJsonLd(article: Article): string {
    const headline = stripTagsAndEntities(article.metaTitle || article.h1[0]?.h || article.selectedKeyword);
    const description = stripTagsAndEntities(article.metaDescription || article.h1[0]?.p || '');
    const bodyParts: string[] = [];
    article.getAllParts().forEach(part => {
        part.value.forEach(block => {
            const heading = stripTagsAndEntities(block.h);
            const paragraph = stripTagsAndEntities(block.p);
            if (heading) bodyParts.push(heading);
            if (paragraph) bodyParts.push(paragraph);
        });
    });
    const articleBody = bodyParts.join('\n\n').slice(0, 8000);
    const keywords = article.allKeywords
        .map(item => item.keyword.trim())
        .filter(Boolean);

    const graph: Array<Record<string, unknown>> = [{
        '@type': 'BlogPosting',
        '@id': (article.url || '#article') + '#blogposting',
        headline,
        description,
        keywords: keywords.join(', '),
        articleBody,
        inLanguage: article.language_code || 'cs',
        mainEntityOfPage: article.url || undefined,
        url: article.url || undefined,
        dateModified: article.eatAttribution?.dateModified
            || article.eatAttribution?.datePublished
            || new Date().toISOString(),
        datePublished: article.eatAttribution?.datePublished || undefined,
        author: article.eatAttribution?.authorName ? {
            '@type': 'Person',
            name: article.eatAttribution.authorName,
            description: article.eatAttribution.authorBio || undefined,
            jobTitle: article.eatAttribution.authorRole || undefined,
        } : undefined,
        contributor: article.eatAttribution?.contributors
            ? article.eatAttribution.contributors.split(/\s*,\s*/).filter(Boolean).map((name) => ({
                '@type': 'Person',
                name,
            }))
            : undefined,
        timeRequired: article.eatAttribution?.readMinutes
            ? `PT${article.eatAttribution.readMinutes}M`
            : undefined,
    }];

    const faqs = (article.questions || []).filter((item: ArticleQuestion) =>
        stripTagsAndEntities(item.question) && stripTagsAndEntities(item.answer)
    );
    if (faqs.length) {
        graph.push({
            '@type': 'FAQPage',
            '@id': (article.url || '#article') + '#faq',
            mainEntity: faqs.map((item: ArticleQuestion) => ({
                '@type': 'Question',
                name: stripTagsAndEntities(item.question),
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: stripTagsAndEntities(item.answer),
                }
            }))
        });
    }

    return JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': graph,
    });
}

export function hasValidSchemaMarkup(jsonLd: string | undefined | null): boolean {
    if (!jsonLd || !jsonLd.trim()) return false;
    try {
        const parsed = JSON.parse(jsonLd);
        return !!parsed && typeof parsed === 'object';
    } catch {
        return false;
    }
}
